<template>
  <q-dialog
    v-model="shown"
  >
    <q-card
      style="max-width: 100vw; max-height: 100vh;"
    >
      <model-table
        ref="modelTable"
        title="Edit Dates"
        :collection="boxOfficeReports"
        :filter-by-data-array="filterByDataArray"
        :columns="columns"
        edit-mode
        :show-add="false"
        :show-edit="false"
        sort-by="weekOf"
        :descending="false"
        submodel
        :show-model-level="false"
        :show-filter="false"
        :paginate="false"
      >
        <template
          #header-buttons
        >
          <q-select
            :model-value="selectedMovie"
            style="min-width: 150px;"
            label="Movie"
            :options="movies.data"
            option-label="name"
            :use-input="$_.isNil(selectedMovie)"
            input-style="search"
            input-debounce="100"
            :loading="loading"
            clearable
            @filter="filterMovies"
            @update:model-value="selectMovie"
          />
          <q-select
            v-model="selectedMovieTerm"
            style="min-width: 150px;"
            label="Movie Term"
            :options="movieTerms.data"
            option-label="format"
            :disable="$_.isNil(queryMovieId)"
            :loading="loading"
          />
        </template>
      </model-table>
      <q-card-actions align="right">
        <q-btn
          label="Save"
          :icon-right="isAnyOverlapping ? 'info' : undefined"
          :color="isAnyOverlapping ? 'negative' : 'primary'"
          :loading="loading"
          :disable="isAnyOverlapping"
          @click="save"
        >
          <q-tooltip v-if="isAnyOverlapping">
            Resolve all overlaps before saving box office dates.
          </q-tooltip>
        </q-btn>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import BoxOfficeReportDateRangeCell from 'src/modules/admin/box-office-report/BoxOfficeReportDateRangeCell'
import ModelTable from 'src/modules/admin/components/ModelTable'

export default {
  name: 'BoxOfficeReportDateEditor',
  components: {
    ModelTable
  },
  props: {
    value: {
      type: Boolean,
      required: false,
      default: false
    },
    movie: {
      type: Object,
      required: false,
      default: null
    },
    movieTerm: {
      type: Object,
      required: false,
      default: null
    }
  },
  emits: ['update:value', 'show', 'hide'],
  models: {
    boxOfficeReports: {
      fields: ['id', 'weekOf', 'startDate', 'endDate', 'isPreview', 'weekNumber', 'status', 'locked'],
      movie: {
        fields: ['id', 'name', 'dateOfFirstShowing'],
        studio: {
          fields: ['id', 'name']
        }
      },
      movieTerm: {
        fields: ['id'],
        showingBadges: {
          fields: ['id', 'displayName']
        }
      },
      variables () {
        return {
          limit: 200,
          movieId: this.queryMovieId,
          movieTermId: this.queryMovieTermId,
          skip: _.isNil(this.queryMovieId) || _.isNil(this.queryMovieTermId)
        }
      }
    },
    movies: {
      fields: ['id', 'name', 'dateOfFirstShowing'],
      variables () {
        return {
          searchString: this.movieSearchString
        }
      }
    },
    movieTerms: {
      fields: ['id'],
      showingBadges: {
        fields: ['id', 'displayName']
      },
      variables () {
        return {
          limit: 50,
          movieId: this.queryMovieId
        }
      }
    }
  },
  data () {
    return {
      shown: false,
      selectedMovie: null,
      selectedMovieTerm: null,
      movieSearchString: null,
      loading: false
    }
  },
  computed: {
    columns () {
      return _.compact([
        { name: 'id', field: bor => bor.id },
        { field: 'weekOf', sortable: true, component: 'full-date' },
        { name: 'weekNumber', field: bor => bor.shortWeekName, component: 'chip' },
        { name: 'status', field: bor => this.statusIcon(bor.status), component: 'icon' },
        { name: 'overlap', field: bor => this.overlapIcon(bor), component: 'icon' },
        { name: 'dateRange', field: bor => bor, editor: { component: BoxOfficeReportDateRangeCell }, align: 'right' }
      ])
    },
    queryMovieId () {
      return this.movie?.id || this.selectedMovie?.id
    },
    queryMovieTermId () {
      return this.movieTerm?.id || this.selectedMovieTerm?.id
    },
    filterByDataArray () {
      if (_.isNil(this.queryMovieId) || _.isNil(this.queryMovieTermId)) {
        return []
      } else if (this.boxOfficeReports.loading) {
        return []
      } else {
        return this.boxOfficeReports.data
      }
    },
    isAnyOverlapping () {
      return _.some(this.boxOfficeReports.data, boxOfficeReport => {
        return this.overlapIcon(boxOfficeReport).color !== 'positive'
      })
    }
  },
  watch: {
    value () {
      this.shown = this.value
    },
    shown () {
      this.$emit('update:value', this.shown)
      if (this.shown) {
        this.$emit('show')
      } else {
        this.$emit('hide')
      }
    },
    'movieTerms.loading': {
      handler () {
        if (!_.isNil(this.queryMovieId) && !this.movieTerms.loading) {
          this.selectedMovieTerm = _.last(this.movieTerms.data)
        }
      }
    }
  },
  methods: {
    show () {
      this.shown = true
    },
    hide () {
      this.shown = false
    },
    async save () {
      this.loading = true
      const results = await this.boxOfficeReports.updateDates(this.boxOfficeReports.data)

      if (_.isEmpty(results.errors)) {
        this.$mh.notify('Updated Dates')
        this.hide()
      } else {
        this.$mh.showErrors(results.errors)
      }

      this.loading = false
    },
    statusIcon (status) {
      if (status === 'complete') return { name: 'check_circle', color: 'positive', tooltip: 'Sent' }
      if (status === 'no-reporting') return { name: 'help', color: 'warning', tooltip: 'Reporting is not configured' }
      if (status === 'due-now') return { name: 'event', color: 'warning', tooltip: 'Due now' }
      if (status === 'overdue') return { name: 'cancel', color: 'negative', tooltip: 'Overdue' }
      if (status === 'upcoming') return { name: 'pending', color: 'grey', tooltip: 'Upcoming' }
    },
    overlapIcon (boxOfficeReport) {
      const overlappingBoxOfficeReport = _.find(this.boxOfficeReports.data, otherBoxOfficeReport => {
        if (boxOfficeReport.id === otherBoxOfficeReport.id) return false

        if (boxOfficeReport.startDate.getTime() <= otherBoxOfficeReport.endDate.getTime() &&
            boxOfficeReport.endDate.getTime() >= otherBoxOfficeReport.startDate.getTime()) {
          return true
        }

        if (boxOfficeReport.weekOf.toDateString() === otherBoxOfficeReport.weekOf.toDateString()) return true

        return false
      })

      if (!_.isNil(overlappingBoxOfficeReport)) {
        if (boxOfficeReport.startDate.getTime() <= overlappingBoxOfficeReport.endDate.getTime() &&
            boxOfficeReport.endDate.getTime() >= overlappingBoxOfficeReport.startDate.getTime()) {
          return {
            name: 'error',
            color: 'negative',
            tooltip: `Overlaps with week from ${this.$i18n.d(overlappingBoxOfficeReport.startDate, 'shortDate')} to ${this.$i18n.d(overlappingBoxOfficeReport.endDate, 'shortDate')} (ID: ${overlappingBoxOfficeReport.id})`
          }
        } else {
          return {
            name: 'error',
            color: 'warning',
            tooltip: `Shares box office week with dates from ${this.$i18n.d(overlappingBoxOfficeReport.startDate, 'shortDate')} to ${this.$i18n.d(overlappingBoxOfficeReport.endDate, 'shortDate')} (ID: ${overlappingBoxOfficeReport.id})`
          }
        }
      }

      return { name: 'info', color: 'positive', tooltip: 'Dates do not overlap' }
    },
    selectMovie (movie) {
      this.selectedMovie = movie
      this.selectedMovieTerm = null
    },
    filterMovies (val, update, _abort) {
      update(() => {
        this.movieSearchString = val
      })
    }
  }
}
</script>
